import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState, startTransition } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useLocalePreferencesSync } from './hooks/useLocalePreferencesSync';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { I18nProvider, useTranslation } from './i18n';
import { LoadingPage } from './components';
import LoadingSpinner from './components/LoadingSpinner';
import Layout from './components/Layout/Layout';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import AppRoutes from './components/AppRoutes/AppRoutes';
import { CookieConsentBanner } from './components/CookieConsent/CookieConsent';
import { useGoals } from './hooks/useGoals';
import { useNavigation } from './hooks/useNavigation';
import { usePageTracking } from './hooks/usePageTracking';
import { useAnalyticsConsent } from './hooks/useAnalyticsConsent';
import { setAnalyticsUserProperties } from './utils/analytics';
import { useDueReminders } from './hooks/useDueReminders';
import { getHashPath, isOAuthConsentHash, parseHashRoute } from './routing/parseHashRoute';
import { SitePage, sitePageUrl } from './utils/siteLinks';
import { flowAwaitsAccount, hadSession, hasStartedFlow, isStartHash, markHadSession } from './onboarding/onboardingStore';
import { readStartParams } from './onboarding/startParams';
import { newGoalHash } from './pages/NewGoal/newGoalHash';
import './App.css';
import './styles/todoShared.css';

// #/privacy and #/terms open the legal page in the UI language
const LEGAL_ROUTE_PAGES: Record<string, SitePage> = {
  '/privacy': 'privacy',
  '/terms': 'terms',
};

const AuthPage = lazy(() => import('./pages/AuthPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const OAuthConsentPage = lazy(() => import('./pages/OAuthConsentPage/OAuthConsentPage'));
const OnboardingPage = lazy(() => import('./onboarding/OnboardingPage'));
const TaskDrawer = lazy(() => import('./components/TaskDrawer/TaskDrawer'));

/** The app's front door: no route, or Home. */
const isLandingHash = (hash: string) => ['', '#', '#/', '#/home'].includes(hash);

/**
 * A signed-out visitor at the front door gets the first-goal flow (T-156) when this browser has
 * one under way or has never had a session; someone who signed in here before gets the login.
 */
const opensFlow = (hash: string) => isStartHash(hash) || (isLandingHash(hash) && (hasStartedFlow() || !hadSession()));



function AppContent() {
  const { state: authState } = useAuth();
  const { t, language } = useTranslation();
  const { setMode } = useTheme();

  useEffect(() => {
    const pref = authState.user?.themePreference;
    if (pref === 'light' || pref === 'dark' || pref === 'system') {
      setMode(pref);
    }
  }, [authState.user?.themePreference, setMode]);

  usePageTracking();
  const [analyticsConsent] = useAnalyticsConsent();
  useEffect(() => {
    if (analyticsConsent === 'granted') setAnalyticsUserProperties({ ui_language: language });
  }, [analyticsConsent, language]);
  useLocalePreferencesSync();
  useDueReminders(authState.isAuthenticated);
  useEffect(() => {
    if (authState.isAuthenticated) markHadSession();
  }, [authState.isAuthenticated]);

  const [currentRoute, setCurrentRoute] = useState(window.location.hash);

  const {
    goals,
    loading,
    error,
    loadGoals,
    addGoal,
    updateGoal,
    deleteGoal,
    toggleFavorite,
    addSubGoal,
    updateSubGoal,
    deleteSubGoal,
    updateTodo,
  } = useGoals(authState.isAuthenticated);

  const {
    activeTab,
    underlayHash,
    hasUnderlay,
    navigateToTab,
    navigationDirection,
    routeKey,
    computeDirection,
    setNavigationDirection,
    goBack,
  } = useNavigation();

  const loadingFallback = useMemo(
    () => <LoadingPage message={t('common.loading')} showLogo={true} />,
    [t]
  );

  const contentFallback = useMemo(
    () => <LoadingSpinner message={t('common.loading')} className="route-loading" />,
    [t]
  );

  const isAnyLoading = authState.isLoading;

  const loadingMessage = useMemo(() => {
    if (authState.isLoading) return t('loading.checkingUser');
    return t('common.loading');
  }, [authState.isLoading, t]);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      const direction = computeDirection(hash);
      setNavigationDirection(direction);
      startTransition(() => {
        setCurrentRoute(hash);
      });
    };

    handleHashChange();

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [computeDirection, setNavigationDirection]);

  useEffect(() => {
    const legalPage = LEGAL_ROUTE_PAGES[getHashPath(currentRoute)];

    if (!legalPage) {
      return;
    }

    window.open(sitePageUrl(legalPage, language), '_blank', 'noopener,noreferrer');

    if (window.location.hash !== '#/home') {
      window.location.hash = '#/home';
    }
  }, [currentRoute, language]);

  const route = useMemo(() => parseHashRoute(currentRoute), [currentRoute]);
  // A task opens as a drawer over the page it came from (T-183)
  const pageRoute = useMemo(() => parseHashRoute(underlayHash), [underlayHash]);
  const taskRoute = route.type === 'todo-detail' && route.subgoalId && route.todoId ? route : null;

  const closeTask = useCallback(() => {
    if (hasUnderlay) {
      window.history.back();
    } else if (taskRoute?.subgoalId) {
      window.location.hash = `#/subgoals/${taskRoute.subgoalId}/todos`;
    }
  }, [hasUnderlay, taskRoute?.subgoalId]);

  const reloadUnderTask = useCallback(() => { void loadGoals(); }, [loadGoals]);

  const goToGoalsTab = useCallback(() => navigateToTab('goals'), [navigateToTab]);

  if (isAnyLoading) {
    return <LoadingPage message={loadingMessage} showLogo={true} />;
  }

  // The emailed verification link signs the user in, so it opens whatever the auth state
  if (currentRoute.startsWith('#/verify-email')) {
    return <Suspense fallback={loadingFallback}><VerifyEmailPage /></Suspense>;
  }

  if (!authState.isAuthenticated && opensFlow(currentRoute)) {
    return <Suspense fallback={loadingFallback}><OnboardingPage hash={currentRoute} /></Suspense>;
  }

  if (authState.isAuthenticated && (isStartHash(currentRoute) || isLandingHash(currentRoute))
    && flowAwaitsAccount(authState.user?.email, currentRoute)) {
    if (window.location.hash !== '#/start/welcome') window.history.replaceState(null, '', '#/start/welcome');
    return (
      <Suspense fallback={loadingFallback}>
        <OnboardingPage hash="#/start/welcome" onDone={() => { void loadGoals(); }} />
      </Suspense>
    );
  }

  // Signed in and coming from riselio.com's goal box (T-165): the goal opens on the new goal
  // page with the AI draft picked, so what was typed on the site is not lost
  if (authState.isAuthenticated && isStartHash(currentRoute)) {
    const { goal } = readStartParams(currentRoute);
    window.location.replace(goal ? newGoalHash({ goal, ai: true }) : '#/home');
    return null;
  }

  if (!authState.isAuthenticated) {
    if (currentRoute.startsWith('#/forgot-password')) {
      return <Suspense fallback={loadingFallback}><ForgotPasswordPage /></Suspense>;
    }
    if (currentRoute.startsWith('#/reset-password')) {
      return <Suspense fallback={loadingFallback}><ResetPasswordPage /></Suspense>;
    }
    // An MCP app sent the user here to connect: after sign-in the hash is still the consent screen
    const notice = isOAuthConsentHash(currentRoute) ? t('oauth.consent.signInNotice') : undefined;
    return <Suspense fallback={loadingFallback}><AuthPage notice={notice} /></Suspense>;
  }

  if (route.type === 'oauth-consent') {
    // Shown on its own: the user is in the middle of connecting another app
    return (
      <ErrorBoundary>
        <Suspense fallback={loadingFallback}><OAuthConsentPage requestId={route.requestId} /></Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Layout
        activeTab={activeTab}
        onTabChange={navigateToTab}
        goals={goals}
        navigationDirection={navigationDirection}
        routeKey={routeKey}
        goBack={goBack}
        overlay={taskRoute && (
          <Suspense fallback={null}>
            <TaskDrawer
              subgoalId={taskRoute.subgoalId!}
              todoId={taskRoute.todoId!}
              onClose={closeTask}
              onChanged={reloadUnderTask}
            />
          </Suspense>
        )}
      >
        <Suspense fallback={contentFallback}>
          <AppRoutes
            route={pageRoute}
            pageHash={underlayHash}
            activeTab={activeTab}
            goals={goals}
            loading={loading}
            error={error}
            loadGoals={loadGoals}
            updateGoal={updateGoal}
            deleteGoal={deleteGoal}
            toggleFavorite={toggleFavorite}
            addSubGoal={addSubGoal}
            updateSubGoal={updateSubGoal}
            deleteSubGoal={deleteSubGoal}
            updateTodo={updateTodo}
            addGoal={addGoal}
            goToGoalsTab={goToGoalsTab}
          />
        </Suspense>
      </Layout>


    </ErrorBoundary>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <I18nProvider>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <AppContent />
              <CookieConsentBanner />
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </I18nProvider>
    </ErrorBoundary>
  );
}

export default App;
